// COGS explorer — pure cost math and roll-ups. No React, no I/O.

import type { ModelRate } from './pricing';
import type { CogsAction, DriverStep, UsageAssumption } from './actions';

/**
 * One observed API call for one user, normalized by src/lib/cogs/sync.ts (or the
 * sample generator). `slot`/`grounded` are set when the normalizer knows which
 * swappable driver the call maps to (so a coaching turn's 3 provider calls each
 * price under their own slot); sample rows omit them and fall back to the action's
 * primary slot.
 */
export interface CogsUsageRow {
  user_id: string;
  action: string; // matches CogsAction.id
  slot?: string; // swappable driver slot this call belongs to
  grounded?: boolean; // apply the selected model's grounding surcharge
  model?: string; // vendor model actually used, for display
  input_tokens?: number;
  output_tokens?: number;
  chars?: number;
  calls?: number; // default 1
  ts?: string;
}

// ── Unit cost of a single model invocation given a usage shape ────────────────

export function costOfUsage(model: ModelRate | undefined, usage: UsageAssumption, grounded = false): number {
  if (!model) return 0;
  const calls = usage.calls ?? 1;

  // Subscription vendors: blended per-credit rate (plan cost ÷ included credits)
  // applied to the credits this usage consumes. Overage is handled at the
  // platform-monthly level (subscriptionMonthlyCost), not per unit.
  if (model.kind === 'subscription') {
    const perCredit = model.includedCredits ? (model.monthlyCost ?? 0) / model.includedCredits : 0;
    const rawUnits = model.unit === 'char' ? (usage.chars ?? 0) : 1; // 'call' unit → one call
    const credits = rawUnits * (model.creditsPerUnit ?? 1) * calls;
    return credits * perCredit;
  }

  let per = 0;
  switch (model.kind) {
    case 'llm':
      per += ((usage.inputTokens ?? 0) / 1e6) * (model.inputPerM ?? 0);
      per += ((usage.outputTokens ?? 0) / 1e6) * (model.outputPerM ?? 0);
      if (grounded) per += model.groundingPerCall ?? 0;
      break;
    case 'embedding':
      per += ((usage.inputTokens ?? 0) / 1e6) * (model.inputPerM ?? 0);
      break;
    case 'per_call':
      per += model.perCall ?? 0;
      break;
    case 'per_char':
      per += ((usage.chars ?? 0) / 1000) * (model.perThousandChars ?? 0);
      break;
    case 'payment':
      // Payment fees are revenue-driven, computed via stripeFee() instead.
      break;
  }
  return per * calls;
}

// ── Modeled unit cost (from default usage assumptions + current selection) ────

export interface StepCost {
  slot: string;
  label: string;
  modelId: string;
  modelLabel: string;
  cost: number;
}

export function stepCost(step: DriverStep, selection: Record<string, string>, byId: Record<string, ModelRate>): StepCost {
  const modelId = selection[step.slot] ?? step.defaultModelId;
  const model = byId[modelId];
  return {
    slot: step.slot,
    label: step.label,
    modelId,
    modelLabel: model?.label ?? modelId,
    cost: costOfUsage(model, step.usage, step.grounded),
  };
}

export interface ActionCost {
  action: CogsAction;
  steps: StepCost[];
  unitCost: number; // COGS for one action
}

export function actionCost(action: CogsAction, selection: Record<string, string>, byId: Record<string, ModelRate>): ActionCost {
  const steps = action.steps.map((s) => stepCost(s, selection, byId));
  return { action, steps, unitCost: steps.reduce((sum, s) => sum + s.cost, 0) };
}

// ── Measured cost: apply the currently-selected model to observed usage ───────
// The "explore how a different model changes cost" lever, applied to real tokens:
// for each row we re-price the observed input/output/chars under whatever model
// is currently selected for that action's first matching slot.

function primarySlotForAction(action: CogsAction): DriverStep | undefined {
  // The token/char-bearing step (LLM/embedding/TTS) — the one usage rows describe.
  return action.steps.find((s) => s.usage.inputTokens || s.usage.outputTokens || s.usage.chars) ?? action.steps[0];
}

export interface MeasuredRowCost {
  row: CogsUsageRow;
  modelId: string;
  cost: number;
}

export function measuredRowCost(
  row: CogsUsageRow,
  actionsById: Record<string, CogsAction>,
  selection: Record<string, string>,
  byId: Record<string, ModelRate>,
): MeasuredRowCost | null {
  // Prefer the normalizer-assigned slot; fall back to the action's primary step.
  let slot = row.slot;
  let grounded = row.grounded ?? false;
  if (!slot) {
    const action = actionsById[row.action];
    const step = action ? primarySlotForAction(action) : undefined;
    if (!step) return null;
    slot = step.slot;
    grounded = step.grounded ?? false;
  }
  const modelId = selection[slot];
  if (!modelId) return null;
  const usage: UsageAssumption = {
    inputTokens: row.input_tokens,
    outputTokens: row.output_tokens,
    chars: row.chars,
    calls: row.calls ?? 1,
  };
  return { row, modelId, cost: costOfUsage(byId[modelId], usage, grounded) };
}

// ── Aggregation helpers ───────────────────────────────────────────────────────

export function median(nums: number[]): number {
  if (nums.length === 0) return 0;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export function mean(nums: number[]): number {
  if (nums.length === 0) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

export interface ActionRollup {
  actionId: string;
  label: string;
  calls: number;
  totalCost: number;
  meanCostPerCall: number;
  medianCostPerCall: number;
}

export interface MeasuredSummary {
  perAction: ActionRollup[];
  perUserTotals: number[]; // total COGS per user across all actions
  grandTotal: number;
  medianUserCost: number;
  meanUserCost: number;
  userCount: number;
  rowCount: number;
}

/** Roll a set of measured rows into per-action and per-user summaries. */
export function summarizeMeasured(
  rows: CogsUsageRow[],
  actionsById: Record<string, CogsAction>,
  selection: Record<string, string>,
  byId: Record<string, ModelRate>,
): MeasuredSummary {
  const perActionCosts: Record<string, number[]> = {};
  const perUser: Record<string, number> = {};
  let grandTotal = 0;
  let rowCount = 0;

  for (const row of rows) {
    const mc = measuredRowCost(row, actionsById, selection, byId);
    if (!mc) continue;
    rowCount++;
    (perActionCosts[row.action] ??= []).push(mc.cost);
    perUser[row.user_id] = (perUser[row.user_id] ?? 0) + mc.cost;
    grandTotal += mc.cost;
  }

  const perAction: ActionRollup[] = Object.entries(perActionCosts).map(([actionId, costs]) => ({
    actionId,
    label: actionsById[actionId]?.label ?? actionId,
    calls: costs.length,
    totalCost: costs.reduce((a, b) => a + b, 0),
    meanCostPerCall: mean(costs),
    medianCostPerCall: median(costs),
  }));
  perAction.sort((a, b) => b.totalCost - a.totalCost);

  const perUserTotals = Object.values(perUser);
  return {
    perAction,
    perUserTotals,
    grandTotal,
    medianUserCost: median(perUserTotals),
    meanUserCost: mean(perUserTotals),
    userCount: perUserTotals.length,
    rowCount,
  };
}

// ── Revenue / margin ──────────────────────────────────────────────────────────

export interface SubscriptionMonthly {
  fixed: number; // the flat monthly plan cost
  overage: number; // cost of credits beyond the allotment
  total: number; // fixed + overage
  creditsUsed: number;
  utilization: number; // creditsUsed / includedCredits (1.0 = at plan limit)
}

/**
 * Monthly cost of a subscription vendor given the month's raw usage (calls or
 * characters). Fixed plan cost is sunk; overage bills on credits over the allotment.
 */
export function subscriptionMonthlyCost(rate: ModelRate, monthlyUnits: number): SubscriptionMonthly {
  const creditsUsed = monthlyUnits * (rate.creditsPerUnit ?? 1);
  const included = rate.includedCredits ?? 0;
  const over = Math.max(0, creditsUsed - included);
  const overage = (over / 1000) * (rate.overagePer1kCredits ?? 0);
  const fixed = rate.monthlyCost ?? 0;
  return { fixed, overage, total: fixed + overage, creditsUsed, utilization: included ? creditsUsed / included : 0 };
}

/** Blended per-credit rate for a subscription vendor (plan cost ÷ included credits). */
export function blendedPerCredit(rate: ModelRate): number {
  return rate.includedCredits ? (rate.monthlyCost ?? 0) / rate.includedCredits : 0;
}

/** Stripe fee on a single charge of `amount`. */
export function stripeFee(amount: number, byId: Record<string, ModelRate>): number {
  const s = byId['stripe'];
  if (!s) return 0;
  return amount * (s.percentFee ?? 0) + (s.fixedFee ?? 0);
}

export interface CreditEconomics {
  revenuePerCredit: number;
  costPerCreditByAction: { actionId: string; label: string; credits: number; unitCost: number; costPerCredit: number; marginPerCredit: number }[];
}

/**
 * Unit economics per credit. `revenuePerCredit` defaults to Pro $/mo ÷ included
 * credits (e.g. $99 / 100 = $0.99). Margin per credit = revenue − COGS per credit.
 */
export function creditEconomics(
  actions: CogsAction[],
  selection: Record<string, string>,
  byId: Record<string, ModelRate>,
  revenuePerCredit: number,
): CreditEconomics {
  return {
    revenuePerCredit,
    costPerCreditByAction: actions
      .filter((a) => a.credits > 0)
      .map((a) => {
        const unitCost = actionCost(a, selection, byId).unitCost;
        const costPerCredit = unitCost / a.credits;
        return {
          actionId: a.id,
          label: a.label,
          credits: a.credits,
          unitCost,
          costPerCredit,
          marginPerCredit: revenuePerCredit - costPerCredit,
        };
      }),
  };
}
